import dayjs from "dayjs";

// Full English month names, matching how `paid_months` is stored on the API.
const MONTH_ORDER = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * `paid_months` may arrive as a real array or as a JSON-encoded string,
 * depending on the endpoint. Normalise it to an array of month names.
 */
export function normalizePaidMonths(paidMonths) {
  if (Array.isArray(paidMonths)) return paidMonths;
  if (typeof paidMonths === "string") {
    try {
      const parsed = JSON.parse(paidMonths);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Derive the coverage window a payment spans from its paid months + year.
 * A payment's months always fall within a single calendar year (quarter based),
 * so `from` = first day of the earliest month, `to` = last day of the latest.
 * Returns { start, end } as dayjs instances, or null when it can't be derived.
 */
export function getCoveragePeriod(paidMonths, year) {
  const months = normalizePaidMonths(paidMonths);
  if (!months.length || !year) return null;

  const indices = months
    .map((m) => MONTH_ORDER.indexOf(String(m).trim()))
    .filter((i) => i >= 0);

  if (!indices.length) return null;

  const startIdx = Math.min(...indices);
  const endIdx = Math.max(...indices);

  return {
    start: dayjs(new Date(Number(year), startIdx, 1)).startOf("month"),
    end: dayjs(new Date(Number(year), endIdx, 1)).endOf("month"),
  };
}

/**
 * Human-readable "from → to" label, e.g. "01 May 2026 → 30 Jun 2026".
 */
export function formatCoveragePeriod(paidMonths, year, fmt = "DD MMM YYYY") {
  const period = getCoveragePeriod(paidMonths, year);
  if (!period) return "—";
  return `${period.start.format(fmt)} → ${period.end.format(fmt)}`;
}
